import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import axios from "axios";
import { getAccessToken } from "@/providers/supabase-auth-provider";

export interface UpdateWalkthroughData {
  key: string;
  completed: boolean;
}

/**
 * Update a walkthrough step for a user
 * @param userId - The user ID
 * @param data - Object containing the key and completed status
 * @returns Promise with the response data
 */
export const updateWalkthrough = async (
  userId: string,
  data: UpdateWalkthroughData,
) => {
  // Get access token for authentication
  const accessToken = await getAccessToken();

  const response = await axios.patch(
    `${process.env.NEXT_PUBLIC_API_URL}/walkthrough/${userId}`,
    data,
    {
      headers: {
        "Content-Type": "application/json",
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
    },
  );
  return response.data;
};

/**
 * Hook to update walkthrough steps for a user
 * @param userId - The user ID
 * @returns Mutation object with methods to update walkthrough
 */
export const useUpdateWalkthrough = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateWalkthroughData) =>
      updateWalkthrough(userId, data),
    onSuccess: async () => {
      // Refresh the session to get updated metadata
      await supabase.auth.refreshSession();

      // Invalidate any relevant queries
      queryClient.invalidateQueries({ queryKey: ["user-profile", userId] });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to update walkthrough",
      );
    },
  });
};
